/**
 * Nova formatting helpers (M4.3): platform-neutral (no JSX), shared by the web
 * and native Nova surfaces. Extracts the schedule expression / event source from
 * a compiled DSL and renders a locale-relative "when".
 */
import type { NovaAutomation } from '@tabbio/types';
import { skipReasonKey } from './nova-run-watch-policy';

/** The raw cron expression for a `cron` (or `condition` poll) trigger, else null. */
export function cronExpressionOf(dsl?: NovaAutomation | null): string | null {
  const trigger = dsl?.trigger;
  if (!trigger) return null;
  const params = trigger.params as unknown as Record<string, unknown>;
  if (trigger.type === 'cron') {
    return (params.cron_expression as string) ?? null;
  }
  if (trigger.type === 'condition') {
    return (params.poll_cron_expression as string) ?? null;
  }
  return null;
}

/** The event-source slug (source · event) for an `event` trigger, else null. */
export function eventSourceOf(dsl?: NovaAutomation | null): string | null {
  const trigger = dsl?.trigger;
  if (trigger?.type === 'event') {
    const params = trigger.params as unknown as Record<string, unknown>;
    const source = params.source as string | undefined;
    const event = params.event_type as string | undefined;
    return [source, event].filter(Boolean).join(' · ') || null;
  }
  return null;
}

/**
 * i18n key for a step type's friendly label (`nova.steps.*`). Pair with
 * {@link humanizeStepType} as the defaultValue so unknown step types still
 * render something readable. Replaces the hardcoded English STEP_LABELS maps
 * the boards used to duplicate (which never localized).
 */
export function stepLabelKey(type?: string | null): string {
  if (!type) return 'nova.steps.draft';
  if (type === 'connector.action') return 'nova.steps.connectorAction';
  const bare = type.replace(/^(nova|tabbio)\./, '');
  return `nova.steps.${bare}`;
}

/** Fallback label for a step type with no i18n entry: "draft_replies" → "Draft replies". */
export function humanizeStepType(type?: string | null): string {
  if (!type) return 'Draft';
  const bare = type.replace(/^(nova|tabbio)\./, '').replace(/^connector\./, '');
  return bare.replace(/[._]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Template glyphs for the "Get started" cards (the templates endpoint carries
 * no icon field). Deliberately quiet, one per template, neutral fallback.
 */
const TEMPLATE_EMOJI: Record<string, string> = {
  'daily-job-scan': '🔍',
  'recruiter-inbox': '✉️',
  'application-follow-up': '📬',
  'cv-freshness-check': '📄',
  'profile-views-digest': '📈',
  'linkedin-post-hunter': '💼',
  'interview-prep': '🎯',
  'skill-drift-alert': '📊',
  'weekly-insights-digest': '📰',
  'watch-a-link': '🔗',
  'read-pdf-weekly': '📑',
  'daily-post-pdf-image': '🖼️',
};

export function templateEmoji(key: string): string {
  return TEMPLATE_EMOJI[key] ?? '🗓️';
}

// ── Cadence (ChatGPT Tasks card overline + schedule line) ────────────────────

export type NovaCadence =
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly'
  | 'custom';

const DOW_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function cronParts(cron?: string | null): string[] | null {
  const parts = cron?.trim().split(/\s+/);
  return parts && parts.length === 5 ? parts : null;
}

/** Bucket a 5-field cron into the card's cadence overline (DAILY / WEEKLY / …). */
export function cadenceOfCronExpression(
  cron?: string | null,
): NovaCadence | null {
  const parts = cronParts(cron);
  if (!parts) return cron ? 'custom' : null;
  const [, hour, dom, , dow] = parts;
  const simpleHour = /^\d{1,2}$/.test(hour);
  if (dom === '*' && dow === '*') return simpleHour ? 'daily' : 'custom';
  if (dom === '*' && (dow === '1-5' || /^MON-FRI$/i.test(dow))) {
    return 'weekdays';
  }
  if (dom === '*' && /^([0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)$/i.test(dow)) {
    return 'weekly';
  }
  if (dow === '*' && /^\d{1,2}(,\d{1,2})*$/.test(dom)) return 'monthly';
  return 'custom';
}

export type NovaTimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

/** The cron's hour bucketed into a friendly time of day, when it is a plain hour. */
export function timeOfDayOfCron(cron?: string | null): NovaTimeOfDay | null {
  const parts = cronParts(cron);
  if (!parts || !/^\d{1,2}$/.test(parts[1])) return null;
  const hour = Number(parts[1]);
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

/** Locale weekday name for a weekly cron's day-of-week field, else null. */
export function weekdayNameOfCron(
  cron: string | null | undefined,
  locale: string,
): string | null {
  const parts = cronParts(cron);
  if (!parts) return null;
  const raw = parts[4].toUpperCase();
  let dow: number | null = null;
  if (/^[0-7]$/.test(raw)) dow = Number(raw) % 7;
  else if (DOW_NAMES.includes(raw)) dow = DOW_NAMES.indexOf(raw);
  if (dow == null) return null;
  // 2024-01-07 was a Sunday; offsetting by dow lands on the wanted weekday.
  const date = new Date(Date.UTC(2024, 0, 7 + dow, 12));
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return DOW_NAMES[dow];
  }
}

/** Locale-relative "when" (e.g. "in 2 hours", "3 days ago"). */
export function formatWhen(
  value: string | Date | null | undefined,
  locale: string,
): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = date.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['day', 86_400_000],
      ['hour', 3_600_000],
      ['minute', 60_000],
    ];
    for (const [unit, ms] of units) {
      if (abs >= ms || unit === 'minute') {
        return rtf.format(Math.round(diffMs / ms), unit);
      }
    }
  } catch {
    // Intl.RelativeTimeFormat unavailable: fall back to a short date.
  }
  return date.toLocaleDateString(locale);
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The status half of a board card's "last run" line. A succeeded run that
 * found nothing reads as "no new matches" (such runs are silent: no
 * notification, push, or email), not as a bare "Succeeded", and a skipped
 * run reads "Skipped" with its reason, never the raw reason code.
 */
export function lastRunStatusLabel(
  t: Translate,
  automation: {
    lastRunStatus?: string | null;
    lastRunMatchesFound?: number | null;
    lastRunErrorSummary?: string | null;
  },
): string | null {
  const status = automation.lastRunStatus;
  if (!status) return null;
  if (status === 'succeeded' && automation.lastRunMatchesFound === 0) {
    return t('nova.runPanel.noResults');
  }
  if (status === 'skipped') {
    return t(skipReasonKey(automation.lastRunErrorSummary));
  }
  return t(`nova.runs.runStatus.${status}`, { defaultValue: status });
}
